import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ImportAppTextsModal } from '@client/components/admin/app-text/ImportAppTextsModal';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import i18n from '@client/i18n/config';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Space, Tag } from 'antd';
import { Download, FileText, Upload } from 'lucide-react';
import { useState } from 'react';
import { AppTextFilters } from './AppTextFilters';

export const Route = createFileRoute('/admin-console/platform-content/comms-and-content/app-text')({
    staticData: {
        breadcrumb: {
            title: 'App text',
        },
        ui: {
            icon: FileText,
            title: 'App text management',
            subtitle: 'Manage runtime-editable text content',
            widthVariant: 'fullWidth',
            menuDescription: 'Manage application text content',
        },
    },
    component: AppTextList,
});

type QueryParams = RouterInputs['admin']['appText']['listAppTexts'];
type AppTextData = RouterOutputs['admin']['appText']['listAppTexts'];
type AppTextRow = NonNullable<AppTextData>['rows'][number];
type AppTextOrderBy = NonNullable<QueryParams['orderBy']>;

function AppTextList() {
    const { trpc, trpcUtils, trpcClient } = useTrpc();
    const navigate = useNavigate();
    const { formatDateTime } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('comms');

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { key: 'asc' },
        where: {},
    });
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const { mutate } = trpc.admin.appText.deleteAppText.useMutation();

    const { data, isPending, error } = trpc.admin.appText.listAppTexts.useQuery(input);

    const handleExport = async () => {
        try {
            setIsExporting(true);
            message.loading('Exporting app texts...');

            const result = await trpcClient.admin.appText.exportAppTexts.query({
                where: input.where,
            });

            // Create a blob from the CSV string and trigger download
            const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            message.destroy();
            message.success(`Exported ${result.count} app text entries`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportSuccess = () => {
        trpcUtils.admin.appText.listAppTexts.invalidate();
    };

    const columnHelper = createColumnHelper<AppTextRow, AppTextOrderBy>();

    const columns = [
        columnHelper.accessor('key', {
            id: 'key',
            header: 'Key',
            sortable: 'key',
            cell: ({ value }) => <span className="font-medium">{value}</span>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('value', {
            id: 'value',
            header: 'Value',
            cell: ({ value }) => <div className="max-w-80 truncate">{value}</div>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('locale', {
            id: 'locale',
            header: 'Locale',
            sortable: 'locale',
            cell: ({ value }) => (
                <Tag variant="outlined" color="blue">
                    {value}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('namespace', {
            id: 'namespace',
            header: 'Namespace',
            sortable: 'namespace',
            cell: ({ value }) => <Tag variant="outlined">{value}</Tag>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('format', {
            id: 'format',
            header: 'Format',
            sortable: 'format',
            cell: ({ value }) => (
                <Tag variant="outlined" color={value === 'HTML' ? 'orange' : value === 'JSON' ? 'purple' : 'default'}>
                    {value}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('description', {
            id: 'description',
            header: 'Description',
            cell: ({ value }) => <div className="max-w-60 truncate">{value || '-'}</div>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated At',
            sortable: 'updatedAt',
            cell: ({ value }) => formatDateTime(value),
            className: 'whitespace-nowrap',
        }),
        columnHelper.actions({
            cell: ({ row }) =>
                !isReadOnly ? (
                    <Space className="flex justify-end">
                        <Button.Delete
                            onConfirm={(callback) =>
                                mutate(
                                    { id: row.id },
                                    {
                                        async onSuccess() {
                                            callback();

                                            trpcUtils.admin.appText.listAppTexts.invalidate();

                                            void i18n.reloadResources(undefined, row.namespace);
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ) : null,
            width: 50,
        }),
    ];

    const tableProps = usePrismaTable<AppTextRow, QueryParams['where'], AppTextOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <ReadOnlyBanner module="comms" />
                <TableToolbar
                    filters={<AppTextFilters onChange={(where) => setInput((prev) => ({ ...prev, where }))} />}
                    actions={
                        <Space>
                            {!isReadOnly && (
                                <Button type="secondary" icon={<Upload />} onClick={() => setImportModalOpen(true)}>
                                    Import
                                </Button>
                            )}
                            <Button type="secondary" icon={<Download />} onClick={handleExport} loading={isExporting}>
                                Export
                            </Button>
                            {!isReadOnly && (
                                <Button.Add.Link
                                    to="/admin-console/platform-content/comms-and-content/app-text/add"
                                    variant="primary"
                                    label="New text entry"
                                />
                            )}
                        </Space>
                    }
                />

                <PrismaTable
                    {...tableProps}
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/admin-console/platform-content/comms-and-content/app-text/edit/$id',
                                    params: { id: String(record.id) },
                                },
                                e,
                            );
                        },
                    })}
                />
                <ImportAppTextsModal
                    open={importModalOpen}
                    onClose={() => setImportModalOpen(false)}
                    onSuccess={handleImportSuccess}
                />
                <Outlet />
            </TRPCErrorBoundary>
        </PageController>
    );
}
